import { App, Button, Popconfirm } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useFormatTimestamp from '../../../hooks/useFormatTimestamp';
import type { FolderPasswordSession } from '../../../interfaces/FoldersApiService';
import { ApiErrorEnum } from '../../../enums/ApiErrorEnum';
import { FoldersService } from '../../../services/foldersApiService';
import { useAppStore } from '../../../store/store';
import { getApiErrorCode } from '../../../utils/apiErrorCode';
import { resolveFolderPasswordApiErrorMessage } from '../../../utils/folderPasswordApiErrors';
import { dateFromTicks } from '../../../utils/utils';
import ListyMetaRow from '../../Common/ListyMetaRow';
import ListyWithEmpty from '../../Common/ListyWithEmpty';
import { Icon } from '../../Core/Icon';

const foldersService = new FoldersService();

export interface FileSessionsFormProps {
  /** When set, show only sessions for this folder; when omitted, show all folder password sessions */
  fileId?: number;
  /** File (folder) name for display when in single-folder mode */
  fileName?: string;
  /** Callback when modal should be closed */
  onClose?: () => void;
}

export default function FileSessionsForm({
  fileId,
  fileName: _fileName,
  onClose,
}: FileSessionsFormProps) {
  const { t } = useTranslation();
  const { message, notification } = App.useApp();
  const formatTimestamp = useFormatTimestamp();
  const removeFilePasswordSession = useAppStore((s) => s.removeFilePasswordSession);

  const [sessions, setSessions] = useState<FolderPasswordSession[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [revokingSessionId, setRevokingSessionId] = useState<number | null>(null);
  // Kept for commented Revoke all button (no backend endpoint yet)
  const [isRevokingAll, setIsRevokingAll] = useState(false);

  const isSingleFolderMode = fileId !== undefined;

  const fetchSessions = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await foldersService.getFolderPasswordSessions();
      // API may return either { sessions: [...] } or the array directly
      const allSessions = Array.isArray(response)
        ? response
        : Array.isArray((response as { sessions?: FolderPasswordSession[] })?.sessions)
          ? (response as { sessions: FolderPasswordSession[] }).sessions
          : [];
      setSessions(
        isSingleFolderMode ? allSessions.filter((s) => s.folderId === fileId) : allSessions,
      );
    } catch (error) {
      console.error('Error fetching folder password sessions:', error);
      notification.error({ message: t('modal.fileSessionsForm.fetchError') });
      setSessions([]);
    } finally {
      setIsLoading(false);
    }
  }, [fileId, isSingleFolderMode, notification, t]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const handleRevokeSession = async (session: FolderPasswordSession) => {
    const folderId = session.folderId;
    const sessionId = session.id;
    setRevokingSessionId(sessionId);
    try {
      await foldersService.deleteFolderPasswordSession(folderId, sessionId);
      removeFilePasswordSession(folderId);
      message.success(t('modal.fileSessionsForm.sessionRevoked', { count: 1 }));
      await fetchSessions();
    } catch (error) {
      console.error('Error revoking folder password session:', error);
      const code = getApiErrorCode((error as { response?: unknown }).response);
      if (
        code === ApiErrorEnum.FolderPasswordSessionNotFound ||
        code === ApiErrorEnum.FolderPasswordSessionForbidden
      ) {
        notification.error({ message: resolveFolderPasswordApiErrorMessage(error, t) });
      } else {
        notification.error({ message: t('modal.fileSessionsForm.revokeError') });
      }
    } finally {
      setRevokingSessionId(null);
    }
  };

  /** Revoke all sessions – backend endpoint not available; used by commented Revoke all button below */
  const handleRevokeAll = async () => {
    setIsRevokingAll(true);
    try {
      // if (isSingleFolderMode && fileId !== undefined) {
      //   await foldersService.deleteFolderPasswordSession(fileId); // would need overload/endpoint for "all"
      // } else {
      //   const list = sessions ?? [];
      //   for (const folderId of [...new Set(list.map((s) => s.folderId))]) {
      //     await foldersService.deleteFolderPasswordSession(folderId);
      //   }
      // }
      message.success(t('modal.fileSessionsForm.allSessionsRevoked'));
      onClose?.();
    } catch (error) {
      console.error('Error revoking all folder password sessions:', error);
      notification.error({ message: t('modal.fileSessionsForm.revokeAllError') });
    } finally {
      setIsRevokingAll(false);
    }
  };

  /** Format .NET ticks as locale date-time string */
  const formatDateFromTicks = (ticks: number) =>
    dateFromTicks(ticks).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  void isRevokingAll;
  void handleRevokeAll;

  return (
    <div className="w-full">
      {isLoading ? (
        <div className="flex w-full items-center justify-center py-8">
          <Icon name="LoaderIcon" className="text-blue-dark h-8 w-8 animate-spin" />
        </div>
      ) : (
        <div className="max-h-[60vh] w-full overflow-y-auto">
          <ListyWithEmpty
            className="w-full"
            items={sessions ?? []}
            emptyText={t('modal.fileSessionsForm.noSessions')}
            rowKey="id"
            itemRender={(session: FolderPasswordSession) => (
              <ListyMetaRow
                className="rounded-md px-2"
                avatar={
                  <div className="bg-gray-light flex h-10 w-10 items-center justify-center rounded-full">
                    <Icon name="HardDriveIcon" className="text-gray-dark h-5 w-5" />
                  </div>
                }
                title={
                  <div className="flex flex-wrap items-center gap-2">
                    {!isSingleFolderMode && (
                      <span className="text-gray-dark text-xs font-medium">
                        {session.folderName}
                      </span>
                    )}
                    <span className="text-blue-dark text-sm font-medium">
                      {session.deviceSign || t('modal.fileSessionsForm.unknownDevice')}
                    </span>
                    {session.ipAddress && (
                      <span className="text-gray-dark text-xs font-normal">
                        ({session.ipAddress})
                      </span>
                    )}
                  </div>
                }
                description={
                  <div className="text-gray-dark space-y-1 text-xs">
                    <div className="flex items-center gap-1">
                      <Icon name="HistoryIcon" className="h-3 w-3" />
                      <span>
                        {t('modal.fileSessionsForm.createdAt')}:{' '}
                        {formatTimestamp(dateFromTicks(session.createdAt))}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Icon name="CalendarClockIcon" className="h-3 w-3" />
                      <span>
                        {t('modal.fileSessionsForm.expiresAt')}:{' '}
                        {formatDateFromTicks(session.expiresAt)}
                      </span>
                    </div>
                  </div>
                }
                actions={
                  <Popconfirm
                    title={t('modal.fileSessionsForm.revokeSessionConfirmTitle')}
                    description={t('modal.fileSessionsForm.revokeSessionConfirmDescription')}
                    onConfirm={() => handleRevokeSession(session)}
                    okText={t('common.confirm')}
                    cancelText={t('common.cancel')}
                    okButtonProps={{ danger: true }}
                  >
                    <Button
                      size="small"
                      danger
                      loading={revokingSessionId === session.id}
                      icon={<Icon name="XIcon" className="h-4 w-4" />}
                    >
                      {t('modal.fileSessionsForm.revokeSession')}
                    </Button>
                  </Popconfirm>
                }
              />
            )}
          />
        </div>
      )}

      {/* Footer only when modal Close (or future revoke-all) is present */}
      {onClose ? (
        <div className="border-gray-medium mt-4 flex justify-between gap-2 border-t pt-4">
          <div className="flex gap-2">
            {/* Revoke all: commented until backend endpoint is available – uncomment button and handleRevokeAll body when API exists */}
            {/* {(sessions?.length ?? 0) > 0 && (
            <Popconfirm
              title={t('modal.fileSessionsForm.revokeAllConfirmTitle')}
              description={
                isSingleFolderMode && _fileName
                  ? t('modal.fileSessionsForm.revokeAllSessionsDescription', { fileName: _fileName })
                  : t('modal.fileSessionsForm.revokeAllFolderSessionsDescription')
              }
              onConfirm={handleRevokeAll}
              okText={t('common.confirm')}
              cancelText={t('common.cancel')}
              okButtonProps={{ danger: true }}
            >
              <Button type="text" danger loading={isRevokingAll}>
                {t('modal.fileSessionsForm.logoutAll')}
              </Button>
            </Popconfirm>
          )} */}
          </div>
          <Button onClick={onClose}>{t('common.close')}</Button>
        </div>
      ) : null}
    </div>
  );
}
